import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebase';
import {
  collectionGroup, query, where, onSnapshot,
  addDoc, serverTimestamp, doc, getDoc, setDoc, updateDoc, arrayUnion, collection
} from 'firebase/firestore';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { FICTION_KEYWORDS } from '../lib/fictionKeywords';
import { normaliseCampaignStatus, ACTIVE_CAMPAIGN_STATUSES } from '../lib/campaignStatus';
import { ensureReaderDoc } from '../lib/ensureReaderDoc';
import {
  BookOpen, Search, Calendar, Users, ChevronDown, ChevronUp,
  Globe, Instagram, Send, CheckCircle, Clock, X, ArrowLeft, Tag,
  Bell, BookPlus, Eye, Rocket
} from 'lucide-react';

// Maps campaign status to reader-facing CTA
const CAMPAIGN_CTA = {
  note_interest: { label: 'Register Interest',            badge: 'bg-blue-100 text-blue-700',    badgeLabel: 'Coming Soon',          section: 'Coming Soon' },
  active:        { label: 'Apply for ARC',                badge: 'bg-green-100 text-green-700',   badgeLabel: 'Open for Applications', section: 'Open for Applications' },
  open:          { label: 'Apply for ARC',                badge: 'bg-green-100 text-green-700',   badgeLabel: 'Open for Applications', section: 'Open for Applications' },
  closed:        { label: 'Join Waitlist & Pre-Order',     badge: 'bg-orange-100 text-orange-700', badgeLabel: 'Waitlist',              section: 'Waitlist — Pre-Order Notifications' },
  live:          { label: 'Add to Bookshelf',              badge: 'bg-teal-100 text-teal-700',     badgeLabel: 'Live',                 section: 'Launched' },
};

// Section ordering
const SECTION_ORDER = ['active', 'note_interest', 'live'];

export default function AvailableArcs({ currentReader, readerData, filterAuthorId = null }) {
  const [campaigns, setCampaigns] = useState([]);
  const [books, setBooks] = useState([]);
  const [authors, setAuthors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeKeywords, setActiveKeywords] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [browseCategory, setBrowseCategory] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  // Detail view
  const [selectedItem, setSelectedItem] = useState(null);
  const [applicationMessage, setApplicationMessage] = useState('');
  const [isApplying, setIsApplying] = useState(false);
  const [applySuccess, setApplySuccess] = useState(false);
  const [myApplications, setMyApplications] = useState({});

  // Waitlist / bookshelf
  const [myWaitlist, setMyWaitlist] = useState({});
  const [isJoiningWaitlist, setIsJoiningWaitlist] = useState(false);
  const [waitlistSuccess, setWaitlistSuccess] = useState(false);
  const [isAddingToBookshelf, setIsAddingToBookshelf] = useState(false);
  const [bookshelfSuccess, setBookshelfSuccess] = useState(false);

  // Load campaigns from all authors (any status except archived)
  useEffect(() => {
    // Query all campaigns — we filter client-side by normalised status
    const q = query(collectionGroup(db, 'campaigns'));
    const unsub = onSnapshot(q, async (snap) => {
      const items = [];
      const authorIds = new Set();
      snap.forEach(d => {
        const data = { id: d.id, ...d.data() };
        const pathParts = d.ref.path.split('/');
        data.authorId = pathParts[1];
        const normalised = normaliseCampaignStatus(data.status);
        // Only include campaigns in valid ARC lifecycle stages
        if (ACTIVE_CAMPAIGN_STATUSES.includes(normalised)) {
          data.normalisedStatus = normalised;
          items.push(data);
          authorIds.add(data.authorId);
        }
      });

      const authorMap = { ...authors };
      for (const authorId of authorIds) {
        if (!authorMap[authorId]) {
          try {
            const authorDoc = await getDoc(doc(db, 'authors', authorId));
            if (authorDoc.exists()) {
              authorMap[authorId] = { id: authorDoc.id, ...authorDoc.data() };
            }
          } catch { /* skip */ }
        }
      }
      setAuthors(authorMap);
      setCampaigns(items);
      setIsLoading(false);
    });
    return unsub;
  }, []);

  // Load books with pre_launch_active or launched status
  useEffect(() => {
    const q = query(
      collectionGroup(db, 'books'),
      where('arcAvailable', '==', true)
    );
    const unsub = onSnapshot(q, async (snap) => {
      const items = [];
      const authorIds = new Set();
      snap.forEach(d => {
        const data = { id: d.id, ...d.data() };
        const pathParts = d.ref.path.split('/');
        data.authorId = pathParts[1];
        // Only show pre_launch_active and launched books
        if (data.status === 'pre_launch_active' || data.status === 'launched') {
          items.push(data);
          authorIds.add(data.authorId);
        }
      });

      const authorMap = { ...authors };
      for (const authorId of authorIds) {
        if (!authorMap[authorId]) {
          try {
            const authorDoc = await getDoc(doc(db, 'authors', authorId));
            if (authorDoc.exists()) {
              authorMap[authorId] = { id: authorDoc.id, ...authorDoc.data() };
            }
          } catch { /* skip */ }
        }
      }
      setAuthors(prev => ({ ...prev, ...authorMap }));
      setBooks(items);
    });
    return unsub;
  }, []);

  // Load reader's existing applications
  useEffect(() => {
    if (!currentReader || campaigns.length === 0) return;
    const checkApplications = async () => {
      const appMap = {};
      for (const campaign of campaigns) {
        try {
          const appRef = doc(db, 'authors', campaign.authorId, 'campaigns', campaign.id, 'applications', currentReader.uid);
          const appDoc = await getDoc(appRef);
          if (appDoc.exists()) {
            appMap[campaign.id] = appDoc.data();
          }
        } catch { /* skip */ }
      }
      setMyApplications(appMap);
    };
    checkApplications();
  }, [campaigns, currentReader]);

  // Load reader's waitlist entries
  useEffect(() => {
    if (!currentReader || books.length === 0) return;
    const checkWaitlist = async () => {
      const wlMap = {};
      for (const book of books) {
        try {
          const wlRef = doc(db, 'authors', book.authorId, 'books', book.id, 'waitlist', currentReader.uid);
          const wlDoc = await getDoc(wlRef);
          if (wlDoc.exists()) {
            wlMap[book.id] = true;
          }
        } catch { /* skip */ }
      }
      setMyWaitlist(wlMap);
    };
    checkWaitlist();
  }, [books, currentReader]);

  // Build unified items list — campaign is primary, book fills gaps
  const unifiedItems = useMemo(() => {
    const items = [];
    const bookIdsWithCampaigns = new Set();

    // Add campaigns (they drive the CTA)
    campaigns.forEach(c => {
      bookIdsWithCampaigns.add(c.bookId);
      const matchingBook = books.find(b => b.id === c.bookId);

      items.push({
        type: 'campaign',
        id: `campaign-${c.id}`,
        campaignId: c.id,
        data: c,
        title: c.bookTitle || c.name || 'Untitled',
        authorId: c.authorId,
        coverUrl: c.bookCoverUrl || matchingBook?.coverUrl || '',
        keywords: matchingBook?.fictionKeywords || c.fictionKeywords || [],
        genres: c.genres || [],
        format: c.arcFormat || '',
        createdAt: c.createdAt?.toMillis?.() || 0,
        closeDate: c.closeDate || '',
        spotsAvailable: c.spotsAvailable,
        spotsRemaining: c.spotsRemaining ?? c.spotsAvailable,
        pitch: c.pitch || matchingBook?.description || '',
        bookId: c.bookId || '',
        // Safety net: if no spots remaining, show as closed regardless of actual status
        campaignStatus: (c.normalisedStatus === 'active' && (c.spotsRemaining ?? c.spotsAvailable) <= 0) ? 'closed' : c.normalisedStatus,
        description: matchingBook?.description || c.pitch || '',
        contentWarnings: matchingBook?.contentWarnings || '',
        pubDate: matchingBook?.pubDate || '',
        pageCount: matchingBook?.pageCount || null,
        requirements: c.requirements || '',
        preferredProfiles: c.preferredProfiles || '',
      });
    });

    // Books that are launched but don't have a campaign (or have no "live" campaign)
    books.forEach(book => {
      if (book.status === 'launched' && !bookIdsWithCampaigns.has(book.id)) {
        items.push({
          type: 'book',
          id: `book-${book.id}`,
          campaignId: null,
          data: book,
          title: book.title || 'Untitled',
          authorId: book.authorId,
          coverUrl: book.coverUrl || '',
          keywords: book.fictionKeywords || [],
          genres: book.genres || [],
          format: '',
          createdAt: book.createdAt?.toMillis?.() || 0,
          closeDate: '',
          spotsAvailable: null,
          pitch: book.description || '',
          bookId: book.id,
          campaignStatus: 'live',
          description: book.description || '',
          contentWarnings: book.contentWarnings || '',
          pubDate: book.pubDate || '',
          pageCount: book.pageCount || null,
          requirements: '',
          preferredProfiles: '',
        });
      }
    });

    return items;
  }, [campaigns, books]);

  // Available keywords for filter
  const availableKeywords = useMemo(() => {
    const kws = new Set();
    unifiedItems.forEach(item => {
      (item.keywords || []).forEach(k => kws.add(k));
    });
    return Array.from(kws).sort();
  }, [unifiedItems]);

  // Filter and sort
  const filteredItems = useMemo(() => {
    let result = [...unifiedItems];

    // Series filter — restrict to a single author when the reader has selected one
    if (filterAuthorId) {
      result = result.filter(item => item.authorId === filterAuthorId);
    }

    if (statusFilter !== 'all') {
      result = result.filter(item => item.campaignStatus === statusFilter);
    }

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(item =>
        item.title.toLowerCase().includes(q) ||
        (authors[item.authorId]?.name || '').toLowerCase().includes(q) ||
        (authors[item.authorId]?.penName || '').toLowerCase().includes(q) ||
        (item.keywords || []).some(k => k.toLowerCase().includes(q))
      );
    }

    if (activeKeywords.length > 0) {
      result = result.filter(item => {
        const itemTags = (item.keywords || []).map(k => k.toLowerCase());
        return activeKeywords.every(kw => itemTags.some(t => t.includes(kw.toLowerCase())));
      });
    }

    return result;
  }, [unifiedItems, searchQuery, activeKeywords, authors, statusFilter, filterAuthorId]);

  // Group by campaign status for sectioned view
  const groupedItems = useMemo(() => {
    const groups = {};
    SECTION_ORDER.forEach(s => { groups[s] = []; });
    filteredItems.forEach(item => {
      const s = item.campaignStatus;
      if (groups[s]) groups[s].push(item);
    });
    return groups;
  }, [filteredItems]);

  // Status counts
  const statusCounts = useMemo(() => {
    const counts = { all: unifiedItems.length };
    SECTION_ORDER.forEach(s => { counts[s] = 0; });
    unifiedItems.forEach(item => {
      if (counts[item.campaignStatus] !== undefined) counts[item.campaignStatus]++;
    });
    return counts;
  }, [unifiedItems]);

  const categories = FICTION_KEYWORDS.map(s => s.category);
  const statusTabs = [
    { id: 'all', label: 'All ARCs' },
    { id: 'active', label: 'Open' },
    { id: 'note_interest', label: 'Coming Soon' },
    { id: 'live', label: 'Launched' },
  ];

  function toggleKeyword(kw) {
    setActiveKeywords(prev =>
      prev.includes(kw) ? prev.filter(k => k !== kw) : [...prev, kw]
    );
  }

  // Apply for an ARC campaign
  async function handleApply(item) {
    if (!currentReader) return;
    setIsApplying(true);
    try {
      // Ensure the applicant has a reader doc keyed to their authUid before
      // writing the application — prevents stranded applicants.
      const reader = await ensureReaderDoc({
        currentReader,
        authorId: item.authorId,
        name: readerData?.firstName
          ? `${readerData.firstName} ${readerData.lastName || ''}`.trim()
          : currentReader.displayName,
      });
      const appRef = doc(db, 'authors', item.authorId, 'campaigns', item.campaignId, 'applications', currentReader.uid);
      await setDoc(appRef, {
        readerId: currentReader.uid,
        readerName: reader.firstName ? `${reader.firstName} ${reader.lastName || ''}`.trim() : currentReader.email,
        readerEmail: currentReader.email,
        readerScore: reader.score || 0,
        arcsCompleted: reader.arcsCompleted || 0,
        genres: reader.preferences?.favoriteGenres || [],
        platforms: reader.socialMedia
          ? Object.entries(reader.socialMedia).filter(([, v]) => v).map(([k]) => k)
          : [],
        message: applicationMessage.trim(),
        status: 'pending',
        appliedAt: serverTimestamp(),
        bookId: item.bookId || '',
        bookTitle: item.title || '',
        authorId: item.authorId,
        authorName: authors[item.authorId]?.penName || authors[item.authorId]?.name || '',
      });
      setMyApplications(prev => ({ ...prev, [item.campaignId]: { status: 'pending' } }));
      setApplySuccess(true);
      setApplicationMessage('');
    } catch (err) {
      console.error('Application error:', err);
    } finally {
      setIsApplying(false);
    }
  }

  // Register interest (note_interest stage)
  async function handleRegisterInterest(item) {
    if (!currentReader) return;
    setIsApplying(true);
    try {
      // Ensure the applicant has a reader doc keyed to their authUid before
      // writing the application — prevents stranded applicants.
      const reader = await ensureReaderDoc({
        currentReader,
        authorId: item.authorId,
        name: readerData?.firstName
          ? `${readerData.firstName} ${readerData.lastName || ''}`.trim()
          : currentReader.displayName,
      });
      const appRef = doc(db, 'authors', item.authorId, 'campaigns', item.campaignId, 'applications', currentReader.uid);
      await setDoc(appRef, {
        readerId: currentReader.uid,
        readerName: reader.firstName ? `${reader.firstName} ${reader.lastName || ''}`.trim() : currentReader.email,
        readerEmail: currentReader.email,
        status: 'interested',
        appliedAt: serverTimestamp(),
        bookId: item.bookId || '',
        bookTitle: item.title || '',
        authorId: item.authorId,
        authorName: authors[item.authorId]?.penName || authors[item.authorId]?.name || '',
      });
      setMyApplications(prev => ({ ...prev, [item.campaignId]: { status: 'interested' } }));
      setApplySuccess(true);
    } catch (err) {
      console.error('Register interest error:', err);
    } finally {
      setIsApplying(false);
    }
  }

  // Join waitlist for closed campaign
  async function handleJoinWaitlist(item) {
    if (!currentReader) return;
    setIsJoiningWaitlist(true);
    try {
      const wlRef = doc(db, 'authors', item.authorId, 'books', item.bookId, 'waitlist', currentReader.uid);
      await setDoc(wlRef, {
        readerId: currentReader.uid,
        readerName: readerData?.firstName ? `${readerData.firstName} ${readerData.lastName || ''}`.trim() : currentReader.email,
        readerEmail: currentReader.email,
        joinedAt: serverTimestamp(),
        notifyPreOrder: true,
        notifyLaunch: true,
      });
      setMyWaitlist(prev => ({ ...prev, [item.bookId]: true }));
      setWaitlistSuccess(true);
    } catch (err) {
      console.error('Waitlist error:', err);
    } finally {
      setIsJoiningWaitlist(false);
    }
  }

  // Add published book to bookshelf
  async function handleAddToBookshelf(item) {
    if (!currentReader || !readerData?.id) return;
    setIsAddingToBookshelf(true);
    try {
      const readerRef = doc(db, 'readers', readerData.id);
      await updateDoc(readerRef, {
        booksReceived: arrayUnion(item.bookId),
      });
      setBookshelfSuccess(true);
    } catch (err) {
      console.error('Add to bookshelf error:', err);
    } finally {
      setIsAddingToBookshelf(false);
    }
  }

  const isOnBookshelf = (bookId) => (readerData?.booksReceived || []).includes(bookId);

  // ── DETAIL VIEW ──
  if (selectedItem) {
    const item = selectedItem;
    const author = authors[item.authorId];
    const cta = CAMPAIGN_CTA[item.campaignStatus] || CAMPAIGN_CTA.note_interest;
    const myApp = item.campaignId ? myApplications[item.campaignId] : null;
    const onWaitlist = myWaitlist[item.bookId];
    const onBookshelf = isOnBookshelf(item.bookId);

    return (
      <div className="space-y-4">
        <button
          onClick={() => { setSelectedItem(null); setApplySuccess(false); setWaitlistSuccess(false); setBookshelfSuccess(false); }}
          className="flex items-center gap-1 text-sm text-teal-600 hover:text-teal-700"
        >
          <ArrowLeft className="h-4 w-4" /> Back to all ARCs
        </button>

        <div className="grid gap-6 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-4">
            <Card className="border-slate-200">
              <CardContent className="p-6">
                <div className="flex gap-4">
                  {item.coverUrl ? (
                    <img src={item.coverUrl} alt="" className="h-40 w-28 rounded-lg object-cover shadow-md" />
                  ) : (
                    <div className="flex h-40 w-28 items-center justify-center rounded-lg bg-slate-100">
                      <BookOpen className="h-8 w-8 text-slate-400" />
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h1 className="text-xl font-bold text-slate-900">{item.title}</h1>
                      <Badge className={`border-0 text-xs ${cta.badge}`}>{cta.badgeLabel}</Badge>
                    </div>
                    <p className="mt-1 text-sm text-slate-600">by {author?.penName || author?.name || 'Unknown Author'}</p>

                    {item.keywords.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {item.keywords.slice(0, 8).map((kw, i) => (
                          <Badge key={i} className="border-0 bg-slate-100 text-slate-700 text-xs">{kw}</Badge>
                        ))}
                        {item.keywords.length > 8 && (
                          <Badge className="border-0 bg-slate-50 text-slate-500 text-xs">+{item.keywords.length - 8} more</Badge>
                        )}
                      </div>
                    )}

                    <div className="mt-3 flex items-center gap-4 text-sm text-slate-500">
                      {item.campaignStatus === 'active' && item.spotsAvailable && (
                        <span className="flex items-center gap-1"><Users className="h-4 w-4" /> {item.spotsRemaining ?? item.spotsAvailable} / {item.spotsAvailable} spots remaining</span>
                      )}
                      {item.format && (
                        <span className="flex items-center gap-1"><BookOpen className="h-4 w-4" /> {item.format}</span>
                      )}
                      {item.pageCount && (
                        <span className="flex items-center gap-1"><BookOpen className="h-4 w-4" /> {item.pageCount} pages</span>
                      )}
                      {item.pubDate && (
                        <span className="flex items-center gap-1"><Calendar className="h-4 w-4" />
                          {item.campaignStatus === 'live' ? 'Published' : 'Launch date'}: {item.pubDate}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {item.description && (
                  <div className="mt-4">
                    <h3 className="text-sm font-semibold text-slate-700">About This Book</h3>
                    <p className="mt-1 text-sm text-slate-600 whitespace-pre-wrap">{item.description}</p>
                  </div>
                )}

                {item.contentWarnings && (
                  <div className="mt-4">
                    <h3 className="text-sm font-semibold text-slate-700">Content Warnings</h3>
                    <p className="mt-1 text-sm text-slate-600">{item.contentWarnings}</p>
                  </div>
                )}

                {item.requirements && (
                  <div className="mt-4">
                    <h3 className="text-sm font-semibold text-slate-700">Requirements</h3>
                    <p className="mt-1 text-sm text-slate-600">{item.requirements}</p>
                  </div>
                )}

                {item.preferredProfiles && (
                  <div className="mt-4">
                    <h3 className="text-sm font-semibold text-slate-700">Preferred Reviewers</h3>
                    <p className="mt-1 text-sm text-slate-600">{item.preferredProfiles}</p>
                  </div>
                )}

                {item.keywords.length > 8 && (
                  <div className="mt-4">
                    <h3 className="text-sm font-semibold text-slate-700">All Keywords</h3>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {item.keywords.map((kw, i) => (
                        <Badge key={i} className="border-0 bg-slate-100 text-slate-700 text-xs">{kw}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* CTA CARD — depends on campaign status */}

            {/* NOTE INTEREST */}
            {item.campaignStatus === 'note_interest' && (
              <Card className="border-blue-200 bg-blue-50/50">
                <CardContent className="p-6">
                  {myApp ? (
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-blue-600" />
                      <div>
                        <p className="font-medium text-slate-900">Interest registered!</p>
                        <p className="text-sm text-slate-500">We'll let you know when ARC applications open.</p>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center">
                      <Eye className="mx-auto mb-2 h-8 w-8 text-blue-600" />
                      <p className="font-medium text-slate-900">Coming Soon</p>
                      <p className="mt-1 text-sm text-slate-500">
                        This ARC isn't open for applications yet. Register your interest to be notified when it opens.
                      </p>
                      <Button
                        className="mt-3 gap-1.5 bg-blue-600 hover:bg-blue-700 text-white"
                        onClick={() => handleRegisterInterest(item)}
                        disabled={isApplying || !currentReader}
                      >
                        <Eye className="h-4 w-4" /> {isApplying ? 'Registering...' : 'Register Interest'}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* ACTIVE — apply */}
            {item.campaignStatus === 'active' && (
              <Card className="border-slate-200">
                <CardContent className="p-6">
                  {myApp ? (
                    <div className="flex items-center gap-3">
                      {myApp.status === 'pending' && (
                        <><Clock className="h-5 w-5 text-blue-500" /><div><p className="font-medium text-slate-900">Application Pending</p><p className="text-sm text-slate-500">You'll be notified when the author reviews your application.</p></div></>
                      )}
                      {myApp.status === 'interested' && (
                        <><Eye className="h-5 w-5 text-blue-500" /><div><p className="font-medium text-blue-700">Interest Registered</p><p className="text-sm text-slate-500">ARCs are now open — would you like to apply?</p>
                          <Button className="mt-2 gap-1.5 bg-teal-600 hover:bg-teal-700 text-white text-xs" onClick={() => handleApply(item)} disabled={isApplying}>
                            <Send className="h-3 w-3" /> Apply Now
                          </Button>
                        </div></>
                      )}
                      {myApp.status === 'approved' && (
                        <><CheckCircle className="h-5 w-5 text-green-500" /><div><p className="font-medium text-green-700">Application Approved!</p><p className="text-sm text-slate-500">Your ARC is on its way.</p></div></>
                      )}
                      {myApp.status === 'declined' && (
                        <><X className="h-5 w-5 text-slate-400" /><div><p className="font-medium text-slate-600">Application Not Selected</p><p className="text-sm text-slate-500">Thank you for your interest.</p></div></>
                      )}
                      {myApp.status === 'waitlisted' && (
                        <><Clock className="h-5 w-5 text-yellow-500" /><div><p className="font-medium text-yellow-700">You're on the Waitlist</p><p className="text-sm text-slate-500">If a spot opens up, you'll be notified.</p></div></>
                      )}
                      {myApp.status === 'removed' && (
                        <div>
                          <div className="flex items-center gap-3">
                            <X className="h-5 w-5 text-slate-400" />
                            <div>
                              <p className="font-medium text-slate-600">You were removed from this ARC</p>
                              <p className="text-sm text-slate-500">You can reapply if you'd like to be considered again.</p>
                            </div>
                          </div>
                          <div className="mt-3">
                            <Textarea value={applicationMessage} onChange={e => setApplicationMessage(e.target.value)}
                              placeholder="Optional: Let the author know why you'd like another chance..." rows={3} className="text-sm" />
                            <Button className="mt-2 gap-1.5 bg-teal-600 hover:bg-teal-700 text-white text-xs"
                              onClick={() => handleApply(item)} disabled={isApplying}>
                              <Send className="h-3 w-3" /> {isApplying ? 'Reapplying...' : 'Reapply for ARC'}
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div>
                      <h3 className="text-sm font-semibold text-slate-700">Apply for this ARC</h3>
                      <Textarea className="mt-2" value={applicationMessage} onChange={e => setApplicationMessage(e.target.value)}
                        placeholder="Optional: Tell the author why you'd like to read this book..." rows={3} />
                      <Button className="mt-3 gap-1.5 bg-teal-600 hover:bg-teal-700 text-white"
                        onClick={() => handleApply(item)} disabled={isApplying || !currentReader}>
                        <Send className="h-4 w-4" /> {isApplying ? 'Applying...' : 'Apply for ARC'}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* CLOSED — waitlist */}
            {item.campaignStatus === 'closed' && (
              <Card className="border-orange-200 bg-orange-50/50">
                <CardContent className="p-6">
                  {onWaitlist || waitlistSuccess ? (
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-orange-600" />
                      <div>
                        <p className="font-medium text-slate-900">You're on the waitlist!</p>
                        <p className="text-sm text-slate-500">We'll notify you when pre-orders open or the book goes live.</p>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center">
                      <Bell className="mx-auto mb-2 h-8 w-8 text-orange-600" />
                      <p className="font-medium text-slate-900">ARC applications are closed</p>
                      <p className="mt-1 text-sm text-slate-500">
                        Join the waitlist to be notified when pre-orders go live or the book launches.
                      </p>
                      <Button
                        className="mt-3 gap-1.5 bg-orange-600 hover:bg-orange-700 text-white"
                        onClick={() => handleJoinWaitlist(item)}
                        disabled={isJoiningWaitlist || !currentReader}
                      >
                        <Bell className="h-4 w-4" /> {isJoiningWaitlist ? 'Joining...' : 'Join Waitlist & Pre-Order'}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* LIVE — add to bookshelf */}
            {item.campaignStatus === 'live' && (
              <Card className="border-teal-200 bg-teal-50/50">
                <CardContent className="p-6">
                  {onBookshelf || bookshelfSuccess ? (
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-5 w-5 text-teal-600" />
                      <div>
                        <p className="font-medium text-slate-900">On your bookshelf!</p>
                        <p className="text-sm text-slate-500">This book is in your bookshelf. Head to My Bookshelf to see it.</p>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center">
                      <Rocket className="mx-auto mb-2 h-8 w-8 text-teal-600" />
                      <p className="font-medium text-slate-900">This book is live!</p>
                      <p className="mt-1 text-sm text-slate-500">
                        Add it to your bookshelf to track your reading and leave a review.
                      </p>
                      <Button
                        className="mt-3 gap-1.5 bg-teal-600 hover:bg-teal-700 text-white"
                        onClick={() => handleAddToBookshelf(item)}
                        disabled={isAddingToBookshelf || !currentReader}
                      >
                        <BookPlus className="h-4 w-4" /> {isAddingToBookshelf ? 'Adding...' : 'Add to My Bookshelf'}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {applySuccess && item.campaignStatus !== 'note_interest' && (
              <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-700">
                Your application has been submitted! The author will review it soon.
              </div>
            )}
          </div>

          {/* Author sidebar */}
          <div>
            {author && (
              <Card className="border-slate-200">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-teal-100 font-medium text-teal-700">
                      {(author.penName || author.name || '?').charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{author.penName || author.name}</p>
                      <p className="text-xs text-slate-500">Author</p>
                    </div>
                  </div>
                  {author.bio && <p className="text-sm text-slate-600 mb-3">{author.bio}</p>}
                  <div className="flex flex-wrap gap-2">
                    {author.website && (
                      <a href={author.website} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1 text-xs text-slate-600 hover:bg-slate-200">
                        <Globe className="h-3 w-3" /> Website
                      </a>
                    )}
                    {author.socialLinks?.instagram && (
                      <span className="flex items-center gap-1 rounded-full bg-slate-100 px-2 py-1 text-xs text-slate-600">
                        <Instagram className="h-3 w-3" /> {author.socialLinks.instagram}
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── LIST VIEW ──
  function renderItemCard(item) {
    const author = authors[item.authorId];
    const cta = CAMPAIGN_CTA[item.campaignStatus] || CAMPAIGN_CTA.note_interest;
    const myApp = item.campaignId ? myApplications[item.campaignId] : null;
    const onWaitlist = myWaitlist[item.bookId];
    const onBookshelf = isOnBookshelf(item.bookId);

    return (
      <Card key={item.id} className="border-slate-200 cursor-pointer hover:shadow-md transition-shadow"
        onClick={() => setSelectedItem(item)}>
        <CardContent className="p-0">
          <div className="flex gap-3 p-4">
            {item.coverUrl ? (
              <img src={item.coverUrl} alt="" className="h-24 w-16 rounded object-cover shadow-sm" />
            ) : (
              <div className="flex h-24 w-16 items-center justify-center rounded bg-slate-100">
                <BookOpen className="h-5 w-5 text-slate-400" />
              </div>
            )}
            <div className="flex-1 min-w-0">
              <h3 className="truncate font-medium text-slate-900">{item.title}</h3>
              <p className="text-xs text-slate-500">by {author?.penName || author?.name || 'Author'}</p>
              <Badge className={`mt-1 border-0 text-[10px] ${cta.badge}`}>{cta.badgeLabel}</Badge>
              {item.keywords.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {item.keywords.slice(0, 3).map((kw, i) => (
                    <Badge key={i} className="border-0 bg-slate-100 text-slate-600 text-[10px]">{kw}</Badge>
                  ))}
                  {item.keywords.length > 3 && (
                    <Badge className="border-0 bg-slate-50 text-slate-500 text-[10px]">+{item.keywords.length - 3}</Badge>
                  )}
                </div>
              )}
              {item.campaignStatus === 'active' && item.spotsAvailable && (
                <p className="mt-1 text-xs text-slate-500">{item.spotsRemaining ?? item.spotsAvailable} / {item.spotsAvailable} spots remaining · {item.format || 'eBook'}</p>
              )}
            </div>
          </div>
          {item.pitch && (
            <p className="px-4 pb-2 text-xs text-slate-500 line-clamp-2">{item.pitch}</p>
          )}
          <div className="border-t border-slate-100 px-4 py-2 flex items-center justify-between">
            <span className="text-[11px] text-slate-400">
              {item.campaignStatus === 'active' && item.closeDate && `Closes ${item.closeDate}`}
              {item.campaignStatus === 'note_interest' && item.pubDate && `Launch: ${item.pubDate}`}
              {item.campaignStatus === 'live' && onBookshelf && (
                <span className="flex items-center gap-1"><CheckCircle className="h-3 w-3 text-teal-500" /> On your bookshelf</span>
              )}
              {item.campaignStatus === 'closed' && onWaitlist && (
                <span className="flex items-center gap-1"><Bell className="h-3 w-3 text-orange-500" /> On waitlist</span>
              )}
            </span>
            {myApp && (
              <Badge className={`border-0 text-[10px] ${
                myApp.status === 'approved' ? 'bg-green-100 text-green-700' :
                myApp.status === 'interested' ? 'bg-blue-100 text-blue-700' :
                myApp.status === 'pending' ? 'bg-blue-100 text-blue-700' :
                myApp.status === 'declined' ? 'bg-red-100 text-red-700' :
                myApp.status === 'removed' ? 'bg-slate-100 text-slate-600' :
                'bg-yellow-100 text-yellow-700'
              }`}>{myApp.status === 'removed' ? 'Can reapply' : myApp.status}</Badge>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Status tabs */}
      <div className="flex flex-wrap gap-1.5 border-b border-slate-200 pb-3">
        {statusTabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setStatusFilter(tab.id)}
            className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
              statusFilter === tab.id
                ? 'bg-teal-600 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {tab.label}
            {statusCounts[tab.id] > 0 && (
              <span className={`ml-1.5 text-xs ${statusFilter === tab.id ? 'text-teal-100' : 'text-slate-400'}`}>
                {statusCounts[tab.id]}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Search + keyword filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input className="pl-9" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search by title, author, or keyword..." />
        </div>
        <Button variant="outline" className="gap-1.5 text-sm"
          onClick={() => setShowFilters(!showFilters)}>
          <Tag className="h-4 w-4" /> Filter by Keywords
          {activeKeywords.length > 0 && (
            <Badge className="ml-1 bg-teal-600 text-white border-0 text-[10px] px-1.5">{activeKeywords.length}</Badge>
          )}
          {showFilters ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        </Button>
      </div>

      {/* Active keyword pills */}
      {activeKeywords.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs font-medium text-slate-500">Filtering by:</span>
          {activeKeywords.map(kw => (
            <button key={kw} onClick={() => toggleKeyword(kw)}
              className="inline-flex items-center gap-1 rounded-full bg-teal-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-teal-700">
              {kw} <X className="h-3 w-3" />
            </button>
          ))}
          <button onClick={() => setActiveKeywords([])} className="text-xs text-slate-500 hover:text-slate-700 ml-1">Clear all</button>
        </div>
      )}

      {/* Keyword filter panel */}
      {showFilters && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3">
          {availableKeywords.length > 0 && (
            <div>
              <p className="text-xs font-medium text-slate-600 mb-2">Keywords on available ARCs:</p>
              <div className="flex flex-wrap gap-1.5">
                {availableKeywords.map(kw => (
                  <button key={kw} onClick={() => toggleKeyword(kw)}
                    className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                      activeKeywords.includes(kw) ? 'bg-teal-600 text-white' : 'bg-white text-slate-700 border border-slate-200 hover:border-teal-400 hover:text-teal-700'
                    }`}>{kw}</button>
                ))}
              </div>
            </div>
          )}
          <div className="border-t border-slate-200 pt-3">
            <p className="text-xs font-medium text-slate-600 mb-2">Browse all keywords by category:</p>
            <div className="flex flex-wrap gap-1">
              {categories.map(cat => (
                <button key={cat} onClick={() => setBrowseCategory(browseCategory === cat ? null : cat)}
                  className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                    browseCategory === cat ? 'bg-teal-600 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-100'
                  }`}>{cat}</button>
              ))}
            </div>
            {browseCategory && (
              <div className="mt-2 max-h-48 overflow-y-auto">
                <div className="flex flex-wrap gap-1.5">
                  {FICTION_KEYWORDS.find(s => s.category === browseCategory)?.keywords.map(kw => (
                    <button key={kw} onClick={() => toggleKeyword(kw)}
                      className={`rounded-full px-2.5 py-1 text-xs font-medium transition-colors ${
                        activeKeywords.includes(kw) ? 'bg-teal-600 text-white' : 'bg-white text-slate-700 border border-slate-200 hover:border-teal-400 hover:text-teal-700'
                      }`}>{kw}</button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Results */}
      {isLoading ? (
        <div className="text-center py-8">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-teal-600" />
          <p className="mt-2 text-sm text-slate-500">Loading available ARCs...</p>
        </div>
      ) : filteredItems.length === 0 ? (
        <Card className="border-dashed border-slate-200">
          <CardContent className="p-8 text-center">
            <BookOpen className="mx-auto mb-3 h-10 w-10 text-slate-300" />
            <p className="text-sm text-slate-500">
              {unifiedItems.length === 0 ? 'No ARCs are currently available. Check back soon!' : 'No ARCs match your filters.'}
            </p>
            {(activeKeywords.length > 0 || statusFilter !== 'all') && (
              <Button variant="outline" size="sm" className="mt-3" onClick={() => { setActiveKeywords([]); setStatusFilter('all'); }}>
                Clear filters
              </Button>
            )}
          </CardContent>
        </Card>
      ) : statusFilter === 'all' ? (
        // Sectioned view
        <div className="space-y-6">
          {SECTION_ORDER.map(status => {
            const items = groupedItems[status];
            if (!items || items.length === 0) return null;
            const cta = CAMPAIGN_CTA[status];
            return (
              <div key={status}>
                <div className="flex items-center gap-2 mb-3">
                  <h2 className="text-lg font-semibold text-slate-900">{cta.section}</h2>
                  <Badge className={`border-0 text-[10px] ${cta.badge}`}>{items.length}</Badge>
                </div>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {items.map(item => renderItemCard(item))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredItems.map(item => renderItemCard(item))}
        </div>
      )}
    </div>
  );
}
